import React, { useContext, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  BillingFrequency,
  ContractPlan,
  ContractPlanStatus,
  ContractPlanType,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  Role,
  Subscription,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import {
  getOrgContractList,
  updateBillingFrequency,
} from "api/endpoints/contracts";
import { getOrgWithSubscription } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  contractBillingFreqMap,
  contractBillingFreqMonthList,
} from "constants/contracts";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import {
  ContractsListContext,
  OrgDataContext,
} from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { retrieveBillingDateOccurrences } from "helpers/contracts";
import { toUUID } from "helpers/s12-helpers";
import {
  convertTimestampToDate,
  convertToTimestamp,
  formatDateTimeDisplay,
} from "helpers/time";
import { UrlParamTypes } from "types/org-display";

const ChangeBillingFrequency = () => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const { contractsList, getCurrentContract, setContractsList } =
    useContext(ContractsListContext);
  const currentContract = getCurrentContract(contractsList);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const currentSCPlan = currentContract?.plans?.find(
    (plan: ContractPlan) =>
      plan.planType === ContractPlanType.PLAN_TYPE_SC_SUBSCRIPTION,
  );

  const subscriptionId = orgData?.subscription[0]?.id || "";
  const currentBillingEndDate =
    (orgData?.subscription[0]?.chargify?.currentPeriodEndsAt &&
      formatDateTimeDisplay(
        convertTimestampToDate(
          orgData?.subscription[0]?.chargify?.currentPeriodEndsAt,
        ),
      )) ||
    "N/A";

  const currentContractTermInMonths =
    (currentContract && currentContract.contractTermInMonths) || 0;

  const getTargetBillingFreqOpts = (contractTermInMonths: number) => {
    let availableOpts: BillingFrequency[] = [];
    contractBillingFreqMonthList.map((_, index) => {
      const frequency = contractBillingFreqMonthList[index][0];
      const months = contractBillingFreqMonthList[index][1];
      const isLessThanCurrentFreqMonths = contractTermInMonths >= months;

      if (
        frequency !== BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED &&
        isLessThanCurrentFreqMonths
      ) {
        availableOpts = [...availableOpts, frequency];
      }
    });

    return availableOpts;
  };

  const currentBillingFreq =
    currentSCPlan?.billingFrequency ||
    BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED;

  const [updatedBillingFrequency, setUpdatedBillingFrequency] = useState(
    BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED,
  );

  const [targetBillingFreqOpts, setTargetBillingFreqOpts] = useState(
    getTargetBillingFreqOpts(currentContractTermInMonths),
  );

  const [updatedBillingEndDate, setUpdatedBillingEndDate] = useState(
    new Date(),
  );

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const isChangeBillingFreqDisabled = () => {
    return (
      isSubscriptionActionLoading ||
      !currentContract ||
      !currentSCPlan ||
      ![
        ContractPlanStatus.PLAN_STATUS_ACTIVE,
        ContractPlanStatus.PLAN_STATUS_TRIALING,
      ].includes(currentSCPlan.status) ||
      currentBillingFreq === BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED
    );
  };

  const handleUpdateSubscriptionState = (updatedSubscription: Subscription) => {
    const newOrgData = { ...orgData } as Role;
    newOrgData.subscription[0] = updatedSubscription;
    setOrgData(newOrgData);
  };

  const handleUpdateBillingFrequency = () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    updateBillingFrequency(
      {
        orgId: toUUID(roleId),
        subscriptionId,
        toBillingFrequency: updatedBillingFrequency,
        billingEndDate: convertToTimestamp(updatedBillingEndDate),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        getOrgWithSubscription(
          {
            roleId,
          },
          callOptionsHeaders(regionCode, roleId),
        )
          .then((resp) => {
            if (resp.role?.subscription && resp.role.subscription.length > 0) {
              handleUpdateSubscriptionState(resp.role.subscription[0]);
            }
          })
          .catch(showError);
      })
      .then(() => {
        getOrgContractList(
          {
            orgId: roleId,
          },
          callOptionsHeaders(regionCode, roleId),
        )
          .then((res) => {
            setContractsList(res);
          })
          .catch(showError);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
        resetState();
      });
  };

  // Weird typing here because radio groups tend to return everything as string
  // and we shouldn't really be using them like this https://github.com/mui/material-ui/issues/8180
  const handleBillingFreqSelection = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const frequency = event.target.value;
    setUpdatedBillingFrequency(parseInt(frequency as string, 10));
    const currentContractStartDate =
      (currentContract?.startDate &&
        convertTimestampToDate(currentContract?.startDate)) ||
      new Date();
    const currentContractEndDate =
      (currentContract?.endDate &&
        convertTimestampToDate(currentContract?.endDate)) ||
      new Date();

    let targetBillingEndDateOpts: Date[] = [currentContractEndDate];
    const occurrences = retrieveBillingDateOccurrences({
      now: new Date(),
      startDate: currentContractStartDate,
      endDate: currentContractEndDate,
      interval: contractBillingFreqMap[frequency as BillingFrequency],
    });

    if (occurrences.length > 0) {
      targetBillingEndDateOpts = occurrences;
    }

    setUpdatedBillingEndDate(targetBillingEndDateOpts[0]);
  };

  const resetState = () => {
    setUpdatedBillingEndDate(new Date());
    setTargetBillingFreqOpts(
      getTargetBillingFreqOpts(currentContractTermInMonths),
    );
    setUpdatedBillingFrequency(BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED);
  };

  const billingFrequencyRadioGroup = () => {
    return (
      <RadioGroup
        onChange={handleBillingFreqSelection}
        value={updatedBillingFrequency}
      >
        <Grid container direction="column">
          {targetBillingFreqOpts.map((freq) => {
            return (
              <FormControlLabel
                key={`${contractBillingFreqMap[freq]}`}
                data-testid={`radio-${contractBillingFreqMap[freq]}-months`}
                value={freq}
                control={<Radio />}
                label={`${contractBillingFreqMap[freq]} months`}
              />
            );
          })}
        </Grid>
      </RadioGroup>
    );
  };

  return (
    <>
      <SubscriptionAction
        title="Billing Frequency:"
        middleElem={
          <Typography data-testid="change-billing-frequency-middle-elem">
            <b>Frequency: </b> {contractBillingFreqMap[currentBillingFreq]}{" "}
            month(s)
            <Tooltip title="Data from Contract Plan" placement="right">
              <IconButton
                size="small"
                sx={{ padding: "2px", marginLeft: "4px" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <br />
            <b>Next billing date: </b> {currentBillingEndDate}
            <Tooltip
              title="Data from Subscription Document (Chargify)"
              placement="right"
            >
              <IconButton
                size="small"
                sx={{ padding: "2px", marginLeft: "4px" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isChangeBillingFreqDisabled()}
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="change-billing-frequency-btn"
          >
            Change Billing Frequency
          </Button>
        )}
      </SubscriptionAction>
      <ConfirmDialog
        title="Change Billing Frequency:"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleUpdateBillingFrequency()}
        onCancel={resetState}
        disabled={
          updatedBillingFrequency ===
          BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        <Grid container spacing={2} sx={classes.spaceTop}>
          {currentContractTermInMonths > 0 && (
            <Grid item xs={12}>
              <Alert
                severity="warning"
                variant="filled"
                data-testid={"warning-panel-billing-frequency"}
              >
                Contract billing frequency should be less or equal to current
                contract term. Next available billing date will be updated.
                Please contact the #business-systems team if you have any
                questions.
              </Alert>
            </Grid>
          )}

          <Grid container item xs={12} justifyContent="flex-start">
            <Typography
              variant="body1"
              data-testid="change-billing-frequency-body"
            >
              Change the billing frequency from{" "}
              <b>{contractBillingFreqMap[currentBillingFreq]} month(s) </b>
              to:
            </Typography>
          </Grid>
          <Grid item>{billingFrequencyRadioGroup()}</Grid>
        </Grid>

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={openSnackbar}
          autoHideDuration={DEFAULT_SNACKBAR_DURATION}
          onClose={() => setOpenSnackbar(false)}
          data-testid="snackbar-change-billing-frequency"
        >
          <Alert severity="success" variant="filled">
            Billing frequency updated
          </Alert>
        </Snackbar>
      </ConfirmDialog>
    </>
  );
};

export default ChangeBillingFrequency;
